<script>
  import { recordingStore } from "@stores/recordingStore.js";
  import { fetchWithSession } from "@utils/fetchWithSession.js";
  import { createEventDispatcher, onMount } from "svelte";
  import { Select } from "paxray-components-svelte"

  const dispatch = createEventDispatcher();
  const sufficientNumberOfRegistrations = 10;

  export let idproject;
  export let showAssignedFilter = false;
  export let selectAssignedOptions = [];

  export let selectedProcess = "";
  export let selectedSubprocess = "";
  export let selectedTask = "";
  export let selectedSubtask = "";
  export let selectedTeam = "";
  export let showTeam = true;
  export let showL4 = true;
  export let showReset = true;
  export let selectedAssignedOption = "";

  let listOfRecordings = [];
  let rawListOfRecordings = [];
  let listOfSubtasks = [];
  let listOfTasks = [];
  let listOfSubprocesses = [];
  let processes = [];

  let displayedSubtaskOptions = [];
  let displayedTaskOptions = [];
  let displayedProcessOptions = [];
  let displayedSubprocessOptions = [];
  let displayedTeamOptions = [];

  let taskOptions = [];
  let subtaskOptions = [];
  let processOptions = [];
  let subprocessOptions = [];
  // Bindbar: Teams mit ihren Subtasks (zugewiesen oder registriert), z. B. fuer Tabellen.
  export let teamOptions = [];
  let projectTeams = [];
  let teamSubtaskAssignments = {};
  let storeInitialized = false;

  $: ({
    processes,
    listOfSubprocesses,
    listOfTasks,
    listOfSubtasks,
    listOfRecordings: rawListOfRecordings,
    _initialized: storeInitialized,
  } = $recordingStore);

  $: listOfRecordings = (rawListOfRecordings || []).map((recording) => ({
    ...recording,
    recorderid: recording.recorderid ?? recording.recording_id,
    teamid: recording.teamid ?? recording.team_id,
    starttimeutc: recording.starttimeutc ?? recording.start_time,
    endtimeutc: recording.endtimeutc ?? recording.end_time,
  }));

  $: setFilterOptions(
    processes,
    listOfSubprocesses,
    listOfTasks,
    listOfSubtasks,
    selectedAssignedOption,
    projectTeams,
    teamSubtaskAssignments,
  );

  $: setDisplayedFilterOptions(
    selectedTeam,
    selectedProcess,
    selectedSubprocess,
    selectedTask,
    selectedSubtask,
  );

  onMount(async () => {
    loadProjectTeams();
    await recordingStore.initialize(idproject);
    await recordingStore.getRecordings(idproject);
  });

  // Teams und ihre Subtasks aus "Set Up Teams", damit der Team-Filter auch
  // Teams ohne Registrierungen kennt. Ohne Zugriff bleiben nur die Teams aus
  // den Registrierungen.
  async function loadProjectTeams() {
    if (!idproject) return;
    try {
      const [teamsResponse, assignmentsResponse] = await Promise.all([
        fetchWithSession(`/api/damprojects/dam-teams?idproject=${idproject}`, {
          cache: "no-store",
        }),
        fetchWithSession(`/api/damprojects/team-collection/${idproject}`, {
          cache: "no-store",
        }),
      ]);
      if (teamsResponse.ok) {
        const teams = await teamsResponse.json();
        projectTeams = Array.isArray(teams)
          ? teams.map((team) => ({ id: team.id, name: team.name }))
          : [];
      }
      if (assignmentsResponse.ok) {
        const collection = await assignmentsResponse.json();
        teamSubtaskAssignments =
          collection?.assignments && typeof collection.assignments === "object"
            ? collection.assignments
            : {};
      }
    } catch (err) {
      console.error(err);
    }
  }

  function setFilterOptions() {
    const getRegistrationCount = (subtask) =>
      (subtask.recordings || []).filter((recording) =>
        listOfRecordings.find(
          (r) => r.recorderid === recording.recording_id && !r.deleted,
        ),
      ).length;

    const matchesRegistrationStatus = (subtask) => {
      const registrationCount = getRegistrationCount(subtask);

      if (selectedAssignedOption === "enough_registrations") {
        return registrationCount >= sufficientNumberOfRegistrations;
      }
      if (selectedAssignedOption === "limited_registrations") {
        return (
          registrationCount > 0 &&
          registrationCount < sufficientNumberOfRegistrations
        );
      }
      if (selectedAssignedOption === "assigned") {
        return registrationCount > 0;
      }
      if (selectedAssignedOption === "no_registrations") {
        return registrationCount === 0;
      }
      if (selectedAssignedOption === "not_assigned") {
        return registrationCount === 0;
      }
      return true;
    };

    if (selectedAssignedOption) {
      subtaskOptions = listOfSubtasks
        .filter(matchesRegistrationStatus)
        .sort((a, b) => a.name.localeCompare(b.name));
    } else {
      subtaskOptions = [...listOfSubtasks].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    }

    if (selectedAssignedOption) {
      taskOptions = listOfTasks
        .filter((task) =>
          task.subtasks.some((st) =>
            subtaskOptions.some((so) => so.id === st.id),
          ),
        )
        .sort((a, b) => a.name.localeCompare(b.name));
    } else {
      taskOptions = [...listOfTasks].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    }

    if (!selectedAssignedOption) {
      subprocessOptions = [...listOfSubprocesses].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    } else {
      subprocessOptions = listOfSubprocesses
        .filter((subprocess) =>
          subprocess.tasks.some((t) =>
            taskOptions.some((to) => to.id === t.id),
          ),
        )
        .sort((a, b) => a.name.localeCompare(b.name));
    }

    if (!selectedAssignedOption) {
      processOptions = [...processes].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    } else {
      processOptions = processes
        .filter((process) =>
          process.subprocesses.some((sp) =>
            subprocessOptions.some((so) => so.id === sp.id),
          ),
        )
        .sort((a, b) => a.name.localeCompare(b.name));
    }

    // Teams aus "Set Up Teams" plus Teams, die nur in Registrierungen vorkommen.
    // Ein Team umfasst seine zugewiesenen Subtasks und alle Subtasks mit
    // eigenen Registrierungen.
    const teamsById = new Map();
    const ensureTeam = (id, name) => {
      if (!id) return null;
      const key = String(id);
      if (!teamsById.has(key)) {
        teamsById.set(key, { id, name: name || "", subtaskIds: new Set() });
      } else if (!teamsById.get(key).name && name) {
        teamsById.get(key).name = name;
      }
      return teamsById.get(key);
    };

    projectTeams.forEach((team) => {
      const entry = ensureTeam(team.id, team.name);
      (teamSubtaskAssignments[String(team.id)] || []).forEach((subtaskId) =>
        entry.subtaskIds.add(String(subtaskId)),
      );
    });

    const subtaskIdByRecorder = new Map();
    listOfSubtasks.forEach((subtask) =>
      (subtask.recordings || []).forEach((recording) =>
        subtaskIdByRecorder.set(recording.recording_id, String(subtask.id)),
      ),
    );
    listOfRecordings.forEach((recording) => {
      if (recording.deleted) return;
      const entry = ensureTeam(recording.teamid, recording.team);
      const subtaskId = subtaskIdByRecorder.get(recording.recorderid);
      if (entry && subtaskId) entry.subtaskIds.add(subtaskId);
    });

    const taskById = new Map(listOfTasks.map((task) => [String(task.id), task]));
    const subprocessById = new Map(
      listOfSubprocesses.map((subprocess) => [String(subprocess.id), subprocess]),
    );
    const subtaskById = new Map(
      listOfSubtasks.map((subtask) => [String(subtask.id), subtask]),
    );

    teamOptions = [...teamsById.values()]
      .map((team) => {
        const taskIds = new Set();
        const subprocessIds = new Set();
        const processIds = new Set();
        team.subtaskIds.forEach((subtaskId) => {
          const task = taskById.get(String(subtaskById.get(subtaskId)?.task_id));
          const subprocess = subprocessById.get(String(task?.subprocess_id));
          if (!task || !subprocess) return;
          taskIds.add(task.id);
          subprocessIds.add(subprocess.id);
          processIds.add(subprocess.process_id);
        });
        return {
          ...team,
          taskIds: Array.from(taskIds),
          subprocesses: Array.from(subprocessIds),
          processes: Array.from(processIds),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    setDisplayedFilterOptions();
  }

  function setDisplayedFilterOptions() {
    const truncateFilterLabel = (value) => {
      const label = String(value || "");
      return label.length > 50 ? `${label.slice(0, 47)}...` : label;
    };

    displayedTeamOptions = [
      ...teamOptions.map((team) => ({
        value: team.id,
        text: team.name,
      })),
    ];

    const activeTeam = selectedTeam
      ? teamOptions.find((team) => String(team.id) === String(selectedTeam))
      : null;

    displayedProcessOptions = processOptions
      .filter((processOption) => {
        if (!selectedTeam) return true;
        return activeTeam?.processes.includes(processOption.id);
      })
      .map((processOption) => ({
        value: processOption.id,
        text: truncateFilterLabel(processOption.name),
      }));

    if (
      selectedProcess &&
      !displayedProcessOptions.some(
        (process) => process.value === selectedProcess,
      )
    ) {
      selectedProcess = "";
      selectedSubprocess = "";
      selectedTask = "";
      selectedSubtask = "";
    }

    displayedSubprocessOptions = subprocessOptions
      .filter((subprocessOption) => {
        return (
          (!selectedProcess ||
            subprocessOption.process_id === selectedProcess) &&
          displayedProcessOptions.some(
            (process) => process.value === subprocessOption.process_id,
          ) &&
          (!selectedTeam ||
            activeTeam?.subprocesses.includes(subprocessOption.id))
        );
      })
      .map((sp) => ({
        value: sp.id,
        text: truncateFilterLabel(sp.name),
      }));

    // Jede Ebene vor der naechsten pruefen: eine Auswahl, die nach einem
    // Wechsel weiter oben nicht mehr passt, leert sonst alle Ebenen darunter.
    if (
      selectedSubprocess &&
      !displayedSubprocessOptions.some(
        (subprocess) => subprocess.value === selectedSubprocess,
      )
    ) {
      selectedSubprocess = "";
      selectedTask = "";
      selectedSubtask = "";
    }

    displayedTaskOptions = taskOptions
      .filter(
        (task) =>
          (!selectedSubprocess || task.subprocess_id === selectedSubprocess) &&
          displayedSubprocessOptions.some(
            (subprocess) => subprocess.value === task.subprocess_id,
          ) &&
          (!selectedTeam || activeTeam?.taskIds.includes(task.id)),
      )
      .map((t) => ({
        value: t.id,
        text: truncateFilterLabel(t.name),
      }));

    if (
      selectedTask &&
      !displayedTaskOptions.some((task) => task.value === selectedTask)
    ) {
      selectedTask = "";
      selectedSubtask = "";
    }

    displayedSubtaskOptions = subtaskOptions
      .filter(
        (subtaskOption) =>
          (!selectedTask || subtaskOption.task_id === selectedTask) &&
          displayedTaskOptions.some(
            (task) => task.value === subtaskOption.task_id,
          ) &&
          (!selectedTeam || activeTeam?.subtaskIds.has(String(subtaskOption.id))),
      )
      .map((st) => ({
        value: st.id,
        text: truncateFilterLabel(st.name),
      }));

    if (
      selectedSubtask &&
      !displayedSubtaskOptions.some(
        (subtask) => subtask.value === selectedSubtask,
      )
    ) {
      selectedSubtask = "";
    }

    dispatch("change", {
      selectedTeam,
      selectedProcess,
      selectedSubprocess,
      selectedTask,
      selectedSubtask,
      selectedAssignedOption,
    });
  }

  export function resetFilters() {
    selectedProcess = "";
    selectedSubprocess = "";
    selectedTask = "";
    selectedSubtask = "";
    selectedTeam = "";
    selectedAssignedOption = "";
    setDisplayedFilterOptions();
  }
</script>

<div class="filter_wrapper">
  <slot name="before-filters" />
  {#if showAssignedFilter}
    {#key displayedTeamOptions}
      <div class="filter_field">      
        <Select
          items={selectAssignedOptions}
          placeholder="All"
          bind:selected={selectedAssignedOption}
          color="dark"
          labelText="Registration status"
        />
      </div>
    {/key}
  {/if}
  {#if showTeam}
    {#key displayedTeamOptions}
      <div class="filter_field">
        <Select
          items={displayedTeamOptions}
          placeholder="All"
          bind:selected={selectedTeam}
          color="dark"
          labelText="Team"
        />
      </div>
    {/key}
  {/if}
  {#key displayedProcessOptions}
    <div class="filter_field hierarchical-filter">
      <Select
        items={displayedProcessOptions}
        placeholder="All"
        bind:selected={selectedProcess}
        color="dark"
        labelText="Process (L1)"
      />
    </div>
  {/key}
  {#key displayedSubprocessOptions}
    <div class="filter_field hierarchical-filter">
      <Select
        items={displayedSubprocessOptions}
        placeholder="All"
        bind:selected={selectedSubprocess}
        color="dark"
        labelText="Subprocess (L2)"
      />
    </div>
  {/key}
  {#key displayedTaskOptions}
    <div class="filter_field hierarchical-filter">
      <Select
        items={displayedTaskOptions}
        placeholder="All"
        bind:selected={selectedTask}
        color="dark"
        labelText="Task (L3)"
      />
    </div>
  {/key}
  {#if showL4}
    {#key displayedSubtaskOptions}
      <div class="filter_field hierarchical-filter">
        <Select
          items={displayedSubtaskOptions}
          placeholder="All"
          bind:selected={selectedSubtask}
          color="dark"
          labelText="Subtask (L4)"
        />
      </div>
    {/key}
  {/if}
  <slot name="after-subtask-filter" />
  <slot />
  {#if showReset}
    <button
      type="button"
      class="reset-filter-button"
      title="Reset filters"
      aria-label="Reset filters"
      on:click={() => {
        resetFilters();
      }}
    >
      <img src="/img/assistant/icons/reset.svg" alt="" />
    </button>
  {/if}
  <slot name="after-reset" />
</div>

<style>
  .filter_wrapper {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 0.38rem;
  }

  .filter_field {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .reset-filter-button {
    cursor: pointer;
    display: flex;
    height: 2.5rem;
    width: 2.5rem;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 0.5rem;
    background-color: var(--pax-black);
    transition: 300ms ease-in-out;
  }

  .reset-filter-button:hover {
    background-color: var(--pax-orange);
  }

  .reset-filter-button img {
    width: 1rem;
    height: 1rem;
  }

</style>
